/**
 * SafeFrame-enabled ad renderers
 * Replaces vulnerable iframe implementations with SafeFrame containers
 */

import { safeFrameHost } from './SafeFrame';
import { createBidstreamMediaSrc } from './utils/bidstream';
import { clearContainer } from './utils/dom';

interface PrebidBid {
  cpm: number;
  width: number;
  height: number;
  bidder: string;
  adUnitId: string;
  adUnitCode: string;
  currency: string;
  creativeId: string;
  adId: string;
  ad: string;
  meta: Record<string, unknown>;
}

interface ApBid {
  slotID: string;
  amzniid: string;
  amznbid: string;
  amznp: string;
  amznsz: string;
  size: string;
}

/**
 * SafeFrame-enabled Prebid ad renderer
 * BEFORE: Basic iframe vulnerable to breakouts
 * AFTER: Secure SafeFrame container
 */
export function renderSafePrebidAd(container: HTMLDivElement, bid: PrebidBid) {
  const safeFrameId = `sf-prebid-${Date.now()}-${Math.random().toString(36).substring(2)}`;

  // The iframe is now cross-origin (sandbox without allow-same-origin), so the
  // parent window cannot reach into its document — swpbjs.renderAd(doc, ...)
  // would throw. Inline `bid.ad` directly via srcdoc so the creative runs
  // inside the SafeFrame with $sf already installed by the host.
  const safeFrame = safeFrameHost.createContainer({
    id: safeFrameId,
    html: bid.ad,
    w: bid.width,
    h: bid.height,
    allowExpansion: false,  // Prevent floating ads
    allowOverlay: false,    // Prevent overlays
    allowPushDown: false,   // Prevent push-down
    sandbox: true,
  });

  clearContainer(container);
  container.appendChild(safeFrame);

  // Analytics tracking
  console.log('SafeFrame Prebid Ad Rendered:', {
    bidder: bid.bidder,
    cpm: bid.cpm,
    size: `${bid.width}x${bid.height}`,
    safeFrameId
  });
}

/**
 * SafeFrame-enabled Saambaa ad renderer 
 * CRITICAL: This is where floating ads are coming from according to Tom
 */
export function renderSafeSaambaaAd(
  container: HTMLDivElement, 
  placement: number | string, 
  floor: number, 
  size: string
) {
  const safeFrameId = `sf-saambaa-${Date.now()}-${Math.random().toString(36).substring(2)}`;
  
  // Saambaa widget HTML - now safely contained
  const saambaaHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { margin: 0; padding: 0; overflow: hidden; }
        </style>
      </head>
      <body>
        <!-- BEGIN SAAMBAA WIDGET EMBED ${size} -->
        ${placement}
        <!-- END SAAMBAA WIDGET EMBED -->
      </body>
    </html>
  `;

  const [width, height] = size.split('x').map(Number);
  
  const safeFrame = safeFrameHost.createContainer({
    id: safeFrameId,
    html: saambaaHTML,
    w: width || 300,
    h: height || 250,
    allowExpansion: false,  // CRITICAL: Block expansion that creates floating ads
    allowOverlay: false,    // CRITICAL: Block overlays
    allowPushDown: false,   // CRITICAL: Block push-down
    sandbox: true
  });

  clearContainer(container);
  container.appendChild(safeFrame);

  console.log('SafeFrame Saambaa Ad Rendered:', {
    placement,
    floor,
    size,
    safeFrameId,
    protection: 'expansion_blocked'
  });
}

/**
 * SafeFrame-enabled Bidstream ad renderer
 * CRITICAL: Another source of problematic ads according to discussion
 */
export function renderSafeBidstreamAd(
  container: HTMLDivElement,
  placement: number | string,
  floor: number,
  size: string
) {
  const safeFrameId = `sf-bidstream-${Date.now()}-${Math.random().toString(36).substring(2)}`;
  const random = Math.floor(Math.random() * 1e8);
  const [width, height] = size.split('x').map(Number);
  
  // Create Bidstream ad HTML that's safely contained
  const bidstreamHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { margin: 0; padding: 0; overflow: hidden; background: transparent; }
          #friendly_${random} { width: 100%; height: 100%; }
        </style>
      </head>
      <body>
        <ins id="friendly_${random}" style="height: 100%; background: transparent;"></ins>
        <script src="${createBidstreamMediaSrc(placement, `friendly_${random}`, random)}"></script>
      </body>
    </html>
  `;

  const safeFrame = safeFrameHost.createContainer({
    id: safeFrameId,
    html: bidstreamHTML,
    w: width || 300,
    h: height || 250,
    allowExpansion: false,  // CRITICAL: Prevent floating ads
    allowOverlay: false,    // CRITICAL: Prevent overlays  
    allowPushDown: false,   // CRITICAL: Prevent push-down
    sandbox: true
  });

  clearContainer(container);
  container.appendChild(safeFrame);

  console.log('SafeFrame Bidstream Ad Rendered:', {
    placement,
    floor,
    safeFrameId,
    protection: 'breakout_blocked'
  });
}

/**
 * SafeFrame-enabled Amazon APS ad renderer
 */
export function renderSafeApAd(container: HTMLDivElement, bid: ApBid) {
  const safeFrameId = `sf-aps-${Date.now()}-${Math.random().toString(36).substring(2)}`;
  
  const apsHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>body { margin: 0; padding: 0; overflow: hidden; }</style>
      </head>
      <body>
        <script>
          var kvBidObject = {
            kvMap: {},
            url: "${window.location.href}",
            bidType: "outstream",
            cv: "v2.0.0"
          };
          
          function _apstagRenderCallback(a) {
            for (var e = window, n = 0; n < 5;) {
              if ((e = a.ampEnv ? window : e.parent).apstag) try {
                n = 5;
                e.apstag.renderImp(document, "${bid.amzniid}", a);
              } catch (a) {}
              n++;
            }
          }
          
          var apstagUrl = "//c.amazon-adsystem.com/aax2/apstag.js";
          var s = document.createElement("script");
          s.onload = function() { _apstagRenderCallback(kvBidObject); };
          s.src = apstagUrl;
          document.head.appendChild(s);
        </script>
      </body>
    </html>
  `;

  const [width, height] = bid.size.split('x').map(Number);
  
  const safeFrame = safeFrameHost.createContainer({
    id: safeFrameId,
    html: apsHTML,
    w: width,
    h: height,
    allowExpansion: false,
    allowOverlay: false,
    allowPushDown: false,
    sandbox: true
  });

  clearContainer(container);
  container.appendChild(safeFrame);
}

/**
 * SafeFrame-enabled PubVentures ad renderer
 */
export function renderSafePubVenturesAd(
  container: HTMLDivElement,
  placement: number | string,
  floor: number,
  size: string
) {
  const safeFrameId = `sf-pubventures-${Date.now()}-${Math.random().toString(36).substring(2)}`;

  const [width, height] = size.split('x').map(Number);
  const pubVenturesHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>body { margin: 0; padding: 0; overflow: hidden; }</style>
      </head>
      <body>
        <!-- BEGIN JS TAG - ${window.location.host} ${size} -->
        <script src="https://secure.adnxs.com/ttj?id=${placement}&cb=%5BCACHEBUSTER%5D"></script>
        <!-- END TAG -->
      </body>
    </html>
  `;

  const safeFrame = safeFrameHost.createContainer({
    id: safeFrameId,
    html: pubVenturesHTML,
    w: width || 300,
    h: height || 250,
    allowExpansion: false,
    allowOverlay: false,
    allowPushDown: false,
    sandbox: true
  });

  clearContainer(container);
  container.appendChild(safeFrame);
}

/**
 * SafeFrame-enabled Opsco ad renderer
 */
export function renderSafeOpscoAd(
  container: HTMLDivElement,
  placement: number | string,
  floor: number,
  size: string
) {
  const safeFrameId = `sf-opsco-${Date.now()}-${Math.random().toString(36).substring(2)}`;
  const [width, height] = size.split('x').map(Number);
  const opscoHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>body { margin: 0; padding: 0; overflow: hidden; }</style>
      </head>
      <body>
        <!-- BEGIN OPSCO TAG -->
        <script type="text/javascript" id="${placement}" src="https://tag.ops.co/${placement}/index.js" charset="utf-8" async></script>
        <!-- END OPSCO TAG -->
      </body>
    </html>
  `;

  const safeFrame = safeFrameHost.createContainer({
    id: safeFrameId,
    html: opscoHTML,
    w: width || 300,
    h: 250,
    allowExpansion: false,
    allowOverlay: false,
    allowPushDown: false,
    sandbox: true
  });

  clearContainer(container);
  container.appendChild(safeFrame);
}

